import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, CardGroup, Card, CardBody, Input } from 'reactstrap';
import { useQuery } from 'react-apollo';
import { FormattedMessage, useIntl } from 'react-intl';
import { get } from 'lodash-es';
import moment from 'moment';
import { Spinner, ProductContainer } from '../../containers';
import PolicyForm from '../../components/PolicyForm';
import { formatDate } from '../../constants/dateFormatting';
import { GetUserPoliciesQuery } from '../../operations/queries/GetUserPoliciesQuery';
import { handleRequestError } from '../../constants/errorCodes';
import { POLICY_STATUS_ACTIVE, POLICY_STATUS_EXPIRED, POLICY_STATUS_RUNNING_OUT } from '../../constants/business';

export const AdminEditPoliciesData = ({ user }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [filterValue, setFilterValue] = useState(POLICY_STATUS_ACTIVE);

  const { data, loading, error } = useQuery(GetUserPoliciesQuery, {
    variables: { order: { date: 'ASC' }, userId: `${user.id}` },
    onError: error => handleRequestError(error, dispatch, { silent: true }),
  });

  let policies = get(data, 'getInsurancePolicy.insurancePolicies', []).sort((a, b) =>
    moment(a.insuranceEnd).isBefore(moment(b.insuranceEnd)) ? 1 : -1
  );
  if (filterValue) {
    policies = get(data, 'getInsurancePolicy.insurancePolicies', []).filter(
      policy => policy.status === filterValue.toLowerCase()
    );
    if (filterValue === POLICY_STATUS_EXPIRED) {
      policies.sort((a, b) => (moment(a.insuranceEnd).isBefore(moment(b.insuranceEnd)) ? 1 : -1));
    } else {
      policies.sort((a, b) => (moment(a.insuranceEnd).isBefore(moment(b.insuranceEnd)) ? -1 : 1));
    }
  }

  const activeStatus = (insuranceEnd, insuranceType) => {
    const timeToExpiration = Math.ceil(
      moment(insuranceEnd)
        .endOf('day')
        .diff(moment(), 'days', true)
    );

    if (timeToExpiration < 0) return POLICY_STATUS_EXPIRED;
    if (timeToExpiration <= 1 || (timeToExpiration <= 30 && insuranceType !== 'PZ')) return POLICY_STATUS_RUNNING_OUT;
    return POLICY_STATUS_ACTIVE;
  };

  const renderPolicies = () => {
    if ((!data && !error) || loading) {
      return <Spinner className="spinner-positioning" />;
    }
    if (policies.length < 1 || error) {
      return (
        <div className="mx-auto title-text">
          <p className="hok-text-dark pr-1">
            <FormattedMessage id="userMyPolicies.noPoliciesFound" />
          </p>
        </div>
      );
    }
    return policies.map(policy => (
      <ProductContainer
        mainTitle={policy.number}
        key={policy.number}
        subtitle={`${formatMessage({ id: 'userMyPolicies.policyData.policyValidTo' })}: ${formatDate(
          policy.insuranceEnd
        )}`}
        activeStatus={activeStatus(policy.insuranceEnd, policy.basicType)}>
        <PolicyForm user={user} policyData={policy} admin={true} />
      </ProductContainer>
    ));
  };

  return (
    <CardGroup>
      <Card className="hok-card">
        <CardBody>
          {get(data, 'getInsurancePolicy.insurancePolicies', []).length > 0 && (
            <Row className="no-gutters mt-4">
              <Col>
                <div className="mx-auto title-text w-100 d-flex align-items-center flex-wrap">
                  <div className="hok-inline-title-filter ml-0 ml-sm-auto mt-3 mt-sm-0">
                    <Input
                      className="hok-inline-title-filter-select"
                      type="select"
                      id="invoiceFilter"
                      value={filterValue}
                      onChange={event => setFilterValue(event.target.value)}>
                      <option value="">{formatMessage({ id: 'all' }).toUpperCase()}</option>
                      <option value={POLICY_STATUS_ACTIVE}>
                        {formatMessage({ id: 'insurancePolicyContainer.filter.active' }).toUpperCase()}
                      </option>
                      <option value={POLICY_STATUS_EXPIRED}>
                        {formatMessage({ id: 'insurancePolicyContainer.filter.expired' }).toUpperCase()}
                      </option>
                    </Input>
                  </div>
                </div>
              </Col>
            </Row>
          )}
          <Row className="no-gutters mt-4">
            <Col>{renderPolicies()}</Col>
          </Row>
        </CardBody>
      </Card>
    </CardGroup>
  );
};
